import { useState, useCallback, useEffect } from 'react';
import { cloneDeep } from '@block/utils';
import { SuccessFile } from '@src/types/self';
import { fetchOcrCode as fetchOcrCodeUtil } from './utils';
import { DEFAULT_ATTACHMENT_TYPE } from '../../consts';
import { getResetData, SPECIAL_ATTACHMENT_FILE_TYPE } from './constants';
import { CertificateData, ConfirmModalState, ImagePreviewState } from './types';
import { CertificateType } from '../../types';

// 表单数据处理钩子
export const useFormDataHandler = (
  initialValue: CertificateData | undefined,
  onChange: (data: CertificateData, type?: 'reset' | 'init') => void,
  formRef: React.RefObject<any>,
  codeItemKey?: string,
  unableReasonList?: any[],
  certificateType?: CertificateType,
  isFullTime?: boolean,
) => {
  const [formState, setFormState] = useState<CertificateData>(initialValue || getResetData(codeItemKey));
  const [fileType, setFileType] = useState<any>(initialValue?.attachmentFileType);

  const defaultAttachmentFileType = DEFAULT_ATTACHMENT_TYPE[certificateType][isFullTime ? 'fullTime' : 'partTime'];
  // attachmentFileType 应该是当前主流程下的默认值
  const resetData = getResetData(codeItemKey, defaultAttachmentFileType);

  // 设置表单数据
  const setFormData = useCallback(
    async (newValue?: Partial<CertificateData>, type?: 'reset' | 'init') => {
      if (!newValue) return;

      // 处理传入的数据，将 reasonType 和 unableUploadReason 转换为 reason 对象
      const { unableUploadReason, reason } = newValue;
      let newReason = reason;
      // 如果传入了 reasonType 或 unableUploadReason，但没有传入 reason 对象，需要构建新reason对象
      if (unableUploadReason !== undefined && !reason) {
        // 根据 unableUploadReason 在 unableReasonList 中找到对应的 reason 对象
        newReason = unableReasonList?.find((item) => item.reasonIndex === newValue.unableUploadReason);
      }

      const updatedData = {
        ...formState,
        ...newValue,
        reason: newReason,
      };

      // 内部state更新
      const { attachmentFileType } = updatedData;
      setFileType(attachmentFileType);
      setFormState(updatedData);
      // 设置表单数据
      await formRef.current?.setAllFieldsValue(updatedData);
      // 初次不需要调用，这样避免onChange执行后，value改变，多次渲染组件
      type !== 'init' && onChange?.(updatedData);
    },
    [formState, onChange, formRef, unableReasonList],
  );

  // 重置表单数据
  const resetFormData = useCallback(async () => {
    // 表单全部变动，清空所有校验状态
    await formRef.current?.clearValidateStatus();
    await setFormData(resetData, 'reset');
  }, [formRef, setFormData, resetData]);

  // 初始化表单数据
  const initialFormData = useCallback(
    async (value: CertificateData) => {
      // 表单全部变动，清空所有校验状态
      await formRef.current?.clearValidateStatus();
      await setFormData(value, 'init');
    },
    [formRef, setFormData],
  );

  // 处理字段值变更
  const handleFieldValueChange = useCallback(
    async (key: string, itemValue: any) => {
      const currentFormData = cloneDeep(formRef.current?.getFieldsValueIncludeHide()) || {};
      const { unableUpload: newUnableUpload } = currentFormData;

      let file = {};

      // 如果无法上传状态改变了，需要清空当前的file
      if (key === 'unableUpload') {
        let attachmentFileType = defaultAttachmentFileType;
        // 如果勾选，则设置为 -999 ，特殊值，由外层重新计算并传入
        if (itemValue) {
          attachmentFileType = SPECIAL_ATTACHMENT_FILE_TYPE;
        }

        const newFormData = {
          ...resetData,
          unableUpload: newUnableUpload,
          attachmentFileType,
        };

        // 表单全部变动，清空所有校验状态
        await formRef.current?.clearValidateStatus();
        // 就算后续选择原因，会重新触发value的变更
        await setFormData(newFormData, 'reset');
        return;
      }

      // 文件 需要手动更新进去
      if (key === 'fileUrl') {
        const { fileId, fileName, fileUrl } = itemValue;
        file = {
          fileId,
          fileName,
          fileUrl,
        };
      }

      if (key === 'reason') {
        // 清空 fileType ，传reason给外面，让外部计算逻辑重新计算 attachmentFileType
        currentFormData.attachmentFileType = null;

        if (!itemValue) {
          // 清空select的时候，需要清空file
          file = {
            fileId: undefined,
            fileName: undefined,
            fileUrl: undefined,
          };
          currentFormData.reason = resetData.reason;
          currentFormData.reasonType = resetData.reasonType;
          currentFormData.unableUploadReason = resetData.unableUploadReason;
          currentFormData.attachmentFileType = SPECIAL_ATTACHMENT_FILE_TYPE;
        } else {
          // 如果是需要上传文件的原因类型，则清空之前的文件
          // reasonIndex已经在Select组件中被转换为number类型
          const { reasonIndex, reasonType } = itemValue;

          // 切换reason，就都清空file
          file = {
            fileId: undefined,
            fileName: undefined,
            fileUrl: undefined,
          };

          // 直接使用itemValue，不需要额外转换
          currentFormData.reason = itemValue;
          currentFormData.reasonType = reasonType;
          currentFormData.unableUploadReason = reasonIndex;
        }
      }

      const updatedData = {
        ...currentFormData,
        ...file,
      };

      await setFormData(updatedData);
    },
    [formRef, setFormData, resetData, defaultAttachmentFileType],
  );

  useEffect(() => {
    // setFormState 内部的 非init 模式，会触发onChange，导致重新渲染，所以需要单独处理
    setFileType(initialValue?.attachmentFileType);
  }, [initialValue]);

  return {
    formState,
    fileType,
    setFormData,
    resetFormData,
    initialFormData,
    handleFieldValueChange,
  };
};

// OCR识别钩子
export const useOcrRecognition = (
  formState: CertificateData,
  formRef: React.RefObject<any>,
  codeItemKey: string,
  onChange: (data: CertificateData) => void,
) => {
  const [confirmModalState, setConfirmModalState] = useState<ConfirmModalState>({
    visible: false,
    currentCode: '',
    ocrCode: '',
  });

  // 显示确认弹窗
  const showConfirmModal = useCallback((currentCode: string, ocrCode: string) => {
    setConfirmModalState({
      visible: true,
      currentCode,
      ocrCode,
    });
  }, []);

  // 处理确认弹窗的确认按钮
  const handleConfirmOk = useCallback(() => {
    const { ocrCode } = confirmModalState;

    // 更新证书编码到表单
    formRef.current?.setFieldsValue({
      [codeItemKey]: ocrCode,
    });

    // 更新内部状态
    const updatedData = {
      ...formState,
      [codeItemKey]: ocrCode,
    };
    // 清空编码空校验提示
    formRef.current?.clearValidateStatus([codeItemKey]);

    // 更新内部state和外部组件
    onChange?.(updatedData);

    // 关闭弹窗
    setConfirmModalState({
      visible: false,
      currentCode: '',
      ocrCode: '',
    });
  }, [confirmModalState, formRef, codeItemKey, formState, onChange]);

  // 处理确认弹窗的取消按钮
  const handleConfirmCancel = useCallback(() => {
    // 不做任何更改，只关闭弹窗
    setConfirmModalState({
      visible: false,
      currentCode: '',
      ocrCode: '',
    });
  }, []);

  // 获取OCR识别的证书编码
  const fetchOcrCode = useCallback(
    async (file: SuccessFile) => {
      // 内部有catch
      const ocrResult = await fetchOcrCodeUtil(file);
      const { ocrCode, ocrNum, ocrMsg } = ocrResult || {};
      if (ocrCode !== '0') {
        // 提示
        console.error('OCR识别失败', ocrMsg);
        return;
      }

      // 获取当前表单中用户填写的证书编码
      const currentCode = formState[codeItemKey];
      // 如果OCR识别到了证书编码，无论用户是否已填写，都弹出确认框
      if (ocrNum && ocrNum !== currentCode) {
        showConfirmModal(currentCode || '', ocrNum);
      }
    },
    [formState, codeItemKey, showConfirmModal],
  );

  return {
    confirmModalState,
    fetchOcrCode,
    handleConfirmOk,
    handleConfirmCancel,
  };
};

// 图片预览钩子
export const useImagePreview = () => {
  const [imagePreviewState, setImagePreviewState] = useState<ImagePreviewState>({
    visible: false,
    imageUrl: '',
    title: '',
  });

  // 打开图片预览Modal
  const openImagePreview = useCallback((imageUrl: string, title: string) => {
    setImagePreviewState({
      visible: true,
      imageUrl,
      title,
    });
  }, []);

  // 关闭图片预览Modal
  const closeImagePreview = useCallback(() => {
    setImagePreviewState((prev) => ({
      ...prev,
      visible: false,
    }));
  }, []);

  return {
    imagePreviewState,
    openImagePreview,
    closeImagePreview,
  };
};
